import { LatLng, LatLngBounds } from "leaflet";
import { BatchEditHistoryEntry, WorkflowState } from "@luzula/luzula-lib";
import PunktartenSeed from "../assets/json/Punktarten.json";
import { BestandBeschreibung } from "./bohrpunkte/bestand/bestand-model";
import { KaAuflage, Kartierverfahren } from "./bohrpunkte/verfahren/verfahren";
import { KaAufnahme } from "./bohrpunkte/verfahren/ka/ka-modell";

export const CURRENT_VERSION = 17;

/**
 * Regex für gültige FBF-Präfixe (z. B. "b", "tp", "Ap", "bp").
 * Erlaubt Aufeinanderfolgen aus: b, g, h, o, p, q, s, Ap, po, rb, tp, w, a, x.
 * Negativer Lookahead `a(?!p)` verhindert, dass "ap" als a+p durchrutscht —
 * "Ap" ist die einzige erlaubte Schreibweise für anthropogen-pflugerogen.
 */
export const FBF_PREFIX_REGEX = "^(?:b|g|h|o|p|q|s|Ap|po|rb|tp|w|a(?!p)|x)*$";

export function isString(v: any): boolean {
	return typeof v === 'string' || v instanceof String;
}

export function isB(bp: AufnahmePunktDocument): boolean {
	// Bohrpunkte (6/9991) UND Gruben ohne Bodenanalyse (1/3) — letztere werden
	// wie Bohrpunkte erfasst (mineralisches Vollprotokoll ohne Festigkeit/Gefuege/
	// Feindurchwurzelung/Probeentnahme).
	const id = idOf(bp?.type);
	return id === 6 || id === 9991 || id === 1 || id === 3;
}

export function isG(bp: AufnahmePunktDocument): boolean {
	// Gruben mit Bodenanalyse — nur 2 (Weisergrube) und 4 (Bodengrube). Die
	// „ohne Bodenanalyse"-Varianten 1+3 sind isB.
	const id = idOf(bp?.type);
	return id === 2 || id === 4;
}

export function isM(bp: AufnahmePunktDocument): boolean {
	return idOf(bp?.type) === 7;
}

export function isTorf(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('h') ?? false;
}

export function isMudde(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('y') ?? false;
}

export function isQuellsediment(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('q') ?? false;
}

/**
 * Zählt die Zeile zur Moormächtigkeit? Entscheidend ist die überwiegende Torfart
 * (erste Spalte).
 *
 * SEA 95 Teil A, Abschnitt 2 (gedruckte S. 88 und 94): „Bei den Mudden werden für
 * die Moormächtigkeit nur die Organomudden berücksichtigt." Silikat- UND
 * Kalkmudden sind Fledd bzw. Quebb (Corg < 30 %) und zählen nicht. Bis 24.09.2026
 * zählte die Kalkmudde hier mit, die Moormächtigkeit fiel dadurch zu groß aus
 * (Nutzer-Befund). Die Quellsediment-Codes kennt die SEA nicht; Nutzer-Entscheidung
 * 24.09.2026: nur das organisch reiche Quellsediment (q-o) zählt, Quellkalk (q-c)
 * und silikatreiches Quellsediment (q-s) nicht. Festgehalten in moormaechtigkeit.spec.ts.
 */
export function isOrganischesSubstrat(z: SubstratZeile): boolean {
	const t = z.torfarten?.[0];
	if (!t) return false; // Keine Torfart → mineralisch (reines Substrat)
	if (t.startsWith('h')) return true;      // Torf
	if (t.startsWith('y-o')) return true;    // Organomudde
	if (t === 'q-o') return true;            // organisch reiches Quellsediment
	return false; // y-c* (Kalkmudde), y-s* (Silikatmudde), q-c, q-s → zählen nicht
}

/**
 * Berechnet die Moormächtigkeit (Gesamttiefe der organischen Substrate).
 * - Mineralische Schicht ≥40 cm: deren Obergrenze = Moormächtigkeit
 * - Mineralische Schicht <40 cm: zählt mit rein, Suche geht weiter (auch als
 *   Deckschicht ganz oben)
 * - Kein mächtiges mineralisches Substrat: Untergrenze des letzten organischen Substrats
 *
 * Die 40-cm-Regel hat keine Stelle in der SEA; sie ist eine Setzung der App
 * (bestätigt vom Nutzer 24.09.2026, „ich weiß es nicht besser").
 */
export function moorMaechtigkeit(substratfolge: SubstratZeile[]): number | undefined {
	if (!substratfolge?.length) return undefined;

	let letzteOrganischeTiefe: number | undefined;

	for (const z of substratfolge) {
		if (isOrganischesSubstrat(z)) {
			letzteOrganischeTiefe = z.tiefe;
		} else {
			// Mineralisches Substrat
			if (z.maechtigkeit >= 40) {
				// Obergrenze = Tiefe minus Mächtigkeit
				return z.tiefe - z.maechtigkeit;
			}
			// <40 cm: zählt mit rein, weiter suchen
		}
	}

	return letzteOrganischeTiefe;
}

export function istLeer(a: any | undefined): boolean {
	return a === undefined || a === null || a === "";
}

export function effNum(a: number): number | undefined {
	return isNaN(a) ? undefined : a;
}

export function dateString(dateStr: string | undefined): string {
	if (!dateStr) return '';
	const date = new Date(dateStr);
	const str = date?.toISOString()?.split('T')[0];
	return str;
}

export function dateUTCString(dateStr: string|number): string {
  return new Date(dateStr).toUTCString();
}
	
export function commentTimeString(time: number) {
	return (new Date(time)).toLocaleString();
}

export function trackByIndex(index: number, obj: any) {
	return index;
}
export function bodenformLabelString(bp: AufnahmePunktDocument): string {
  if (!bp || !bp.header) return '';
  const hasFbf = bp.header.bodenform?.bf?.some((b: BodenformPair) => !!b.fbf);
  const bf = hasFbf ? bodenformString(bp.header.bodenform, 'fbf') : bodenformString(bp.header.bodenform, 'hbf', true);
  const ws = wasserformString(bp, true, true);  // <-- true für filter7
  const rg = bp.header.bodenform?.reliktG ? '(G)' : '';
  const mk = bp.extendedHeader?.mesoklima ? `-${bp.extendedHeader.mesoklima}` : '';
  const sf = bp.header.substratFeuchte ? `-${bp.header.substratFeuchte}` : '';
  return `${bf}${ws}${rg}${mk}${sf}`;
}

export function hatHumusForm(bp: AufnahmePunktDocument): boolean {
	return idOf(bp.type) !== 7;
}

export function hatVegetationsformengruppe(bp: AufnahmePunktDocument): boolean {
	return idOf(bp.type) === 7;
}

export function humusformLabel(bp: AufnahmePunktDocument): string {
	if (hatHumusForm(bp) && hatVegetationsformengruppe(bp))
		return "HF/VegFG";
	else if (hatHumusForm(bp))
		return "HF";
	else if (hatVegetationsformengruppe(bp))
		return "VegFG";
	else 
		return "";
}

export function bodenformString(bf: Bodenform|undefined, which: string, withPrefixSuffix = false) {
	const includePrefixSuffix = which === 'fbf' || withPrefixSuffix;
	let result = '';
	// Legacy-Fallback: globaler Bodenform.prefix wird nur verwendet, wenn kein Glied
	// einen eigenen Präfix führt (Migration v9→v10 verschiebt ihn nach bf[0].prefix).
	const hasPerPairPrefix = bf?.bf?.some(p => !!p.prefix);
	bf?.bf?.forEach((pair: BodenformPair, index: number) => {
		if (!(pair as any)[which]) return;
		if (index > 0) result += ' ' + pair.trenner;
		const sep = result.length > 0 ? ' ' : '';
		let segment = (pair as any)[which] as string;
		if (includePrefixSuffix && pair.prefix) {
			segment = pair.prefix + segment;
		}
		if (index === 0 && includePrefixSuffix && !hasPerPairPrefix && bf?.prefix) {
			segment = bf.prefix + segment;
		}
		result += sep + segment;
	});
	// Suffix in der FBF nur unterdrücken, wenn das Substrat bereits im
	// begrabenen (letzten) Glied steckt. Suffix-Substrat = Suffix ohne "/",
	// kleingeschrieben (Tiefenstufe egal: /L und /l → "l"). HBF-Substrat =
	// Token vor dem ersten Leerzeichen, ggf. an "/" geteilt für Verbund-HBF
	// wie "l/k F". So bleibt /L erhalten, wenn ein begrabener Sandboden eine
	// echte Lehmunterlagerung darunter hat — analog für /E, /K, /KL, /T,
	// /S, /KS, /O, /C, /KO und alle Tief-Varianten.
	const suffixCode = (bf?.suffix || '').replace(/^\//, '').toLowerCase();
	const buriedCodes = (bf?.bf?.length ?? 0) > 1
		? (bf!.bf[bf!.bf.length - 1].hbf || '').trim().split(/\s+/)[0]
			.split('/').map(s => s.toLowerCase()).filter(Boolean)
		: [];
	const suffixRedundant = !!suffixCode && buriedCodes.includes(suffixCode);
	if (includePrefixSuffix && !!bf?.suffix && !(which === 'fbf' && suffixRedundant)) result += bf.suffix;

	if (which === 'fbf')
		result = result.replaceAll(' ', '');

	return result;
}

function toSuperscript(s: string | number | undefined | null): string {
  if (s === undefined || s === null) return '';
  const str = String(s);
  return str.replace(/\+/g, '⁺').replace(/x/gi, 'ˣ');
}

export function wasserformString(bp: AufnahmePunktDocument, compact = false, filter7 = false): string {
  if (!bp?.header) {
    return '';
  }
    // 7er rausfiltern nur wenn filter7 = true (Vergleich mit == für Kompatibilität mit alten number-Daten)
  const wf1 = filter7 && bp.header.wasserstufe == '7' ? null : bp.header.wasserstufe;
  const wf2 = filter7 && bp.header.wasserstufe2 == '7' ? null : bp.header.wasserstufe2;

  if (!wf1 && !wf2) {
    return '';
  }
  if (wf1 && wf2) {
    const sp = compact ? '' : ' ';
    return `${toSuperscript(wf2)}${sp}/${sp}${toSuperscript(wf1)}`;
  }
  return toSuperscript(wf1 || wf2 || '');
}

export enum LocationSystem {
	EPSG25833 = 'EPSG:25833',
	EPSG25832 = 'EPSG:25832',
	EPSG4326 = 'EPSG:4326',
	EPSG4258 = 'EPSG:4258',
	EPSG3034 = 'EPSG:3034',
	EPSG3035 = 'EPSG:3035',
	EPSG3044 = 'EPSG:3044',
	EPSG3045 = 'EPSG:3045',
	EPSG3857 = 'EPSG:3857',
	EPSG4839 = 'EPSG:4839',
	EPSG5650 = 'EPSG:5650',
	EPSG4647 = 'EPSG:4647',
	// Gauß-Krüger Zonen
	EPSG31466 = 'EPSG:31466', // GK Zone 2
	EPSG31467 = 'EPSG:31467', // GK Zone 3
	EPSG31468 = 'EPSG:31468', // GK Zone 4
	EPSG31469 = 'EPSG:31469'  // GK Zone 5
}

export interface AltNeu<T> {
	alt?: T, neu?: T
}

export interface Location {
	system: LocationSystem,
	x: number, y: number
}

export interface ForstAdresse {
	gemarkung: string,
	flur?: number,
	flurstueck?: string,
	abteilung: string,
	unterabteilung: string,
	teilflaeche: string,
	hilfsflaeche?: string
}

export interface Geologie {
	oben: string,
	trenner?: '/' | '//',
	unten?: string
}

export interface Aufnahme {
	datum: string,
	kartierer: string,
	tiefe?: number
}

// Projekt-Zuordnung für Bohrpunkte
export interface ProjectAssignment {
	id: string;       // Projekt-Dokument _id
	name: string;     // Projektname (für Filterung ohne Lookup)
}

export interface Header {
	nummer: string,
	forstamt: string,
	forstbetrieb?: string,
	revier: string,
	adresse: ForstAdresse,
	koordinaten: Location,

	aufnahme: Aufnahme,
	bodenform: Bodenform,
	humusform: HumusForm,
	wasserstufe?: string, // Grundwasserstufe, steht hinten //
	wasserstufe2?: string, //Stauwasserstufe, sthet vorn //
	vegetationsformengruppe: string,

	substratFeuchte: string,

	bemerkungen: string,

	moorMaechtigkeit?: number,

	// Projekt-Zuordnungen (0-n)
	projects?: ProjectAssignment[],

	labornummer?: string,
	statusLaboranalyse?: '' | 'noch nicht ausgewertet' | 'Ergebnis liegt vor',
}

// Neigungsstufen nach SEA-Klassifikation
export type Neigungsstufe =
	| ''
	| 'Ebene'                          // ≤ 1°
	| 'schwächer geneigter Flachhang'  // > 1...3° (Rampe)
	| 'stärker geneigter Flachhang'    // > 3...7°
	| 'schwächer geneigter Lehnhang'   // > 7...15°
	| 'stärker geneigter Lehnhang'     // > 15...25°
	| 'schwächer geneigter Steilhang'  // > 25...60°
	| 'stärker geneigter Steilhang';   // > 60°

// Wölbungstypen (vertikal x horizontal)
export type Woelbung =
	| ''
	| 'xx'  // doppelt konvex
	| 'xg'  // konvex-gestreckt
	| 'xv'  // konvex-konkav
	| 'gx'  // gestreckt-konvex
	| 'gg'  // doppelt gestreckt
	| 'gv'  // gestreckt-konkav
	| 'vx'  // konkav-konvex
	| 'vg'  // konkav-gestreckt
	| 'vv'; // doppelt konkav

// Einbindung für Ebenen/Rampen
export type EinbindungEbene =
	| ''
	| 'Weitlage'
	| 'Horstlage'
	| 'Rückenlage'
	| 'Riedellage'
	| 'Schulterlage'
	| 'Simslage'
	| 'Fußlage'
	| 'Nischenlage'
	| 'Senkenlage'
	| 'Strecksenkenlage'
	| 'Tallage';

// Hanganschluss für stärker geneigte Hänge
export type Hanganschluss =
	| ''
	| 'B-Lage'   // Böschungslage (Ebene-Ebene)
	| 'V-Lage'   // Vollrelieflage (Kuppe-Ebene)
	| 'H-Lage'   // Hohlrelieflage (Ebene-Senke)
	| 'D-Lage';  // Doppellage (Kuppe-Senke)

// Hangteil bei größerer Höhenspanne
export type Hangteil =
	| ''
	| 'Oberhang'
	| 'Mittelhang'
	| 'Unterhang';

// Innere Rauhheit
export type InnereRauhheit =
	| ''
	| 'glatt'
	| 'leicht bewegt'
	| 'kleinwellig'
	| 'mittelwellig'
	| 'großwellig'
	| 'kupstig'
	| 'küstendünig'
	| 'strandwalldünig'
	| 'altdünig'
	| 'kleinkuppig'
	| 'mittelkuppig'
	| 'großkuppig'
	| 'kleinbucklig'
	| 'mittelbucklig'
	| 'großbucklig'
	| 'kleingieserartig'
	| 'mittelgieserartig'
	| 'ungeordnet versteilt'
	| 'horizontal gestuft'
	| 'hangfaltig'
	| 'hangfurchig';

// Hangrichtung (16 Kompass-Richtungen)
export type Hangrichtung =
	| ''
	| 'N' | 'NNO' | 'NO' | 'ONO'
	| 'O' | 'OSO' | 'SO' | 'SSO'
	| 'S' | 'SSW' | 'SW' | 'WSW'
	| 'W' | 'WNW' | 'NW' | 'NNW';

export interface Relief {
	// Basis-Relief (B-Punkte, Legacy)
	relief?: string,
	reliefUmgebung?: string,

	// Moor-Punkte
	reliefBestand?: string,
	reliefBodenvegetation?: string,

	// Erweiterte Relief-Attribute nach SEA
	neigungsstufe?: Neigungsstufe,
	neigungGrad?: number,              // Exakter Neigungswert in Grad
	woelbung?: Woelbung,
	hangrichtung?: Hangrichtung,

	// Einbindung in Nachbarschaft
	einbindungEbene?: EinbindungEbene, // Für Ebenen und Rampen
	hanganschluss?: Hanganschluss,     // Für stärker geneigte Hänge
	hangteil?: Hangteil,               // Ober-/Mittel-/Unterhang
	hoehenspanne?: number,             // Höhenspanne des Gesamthangs in m

	// Innere Rauhheit
	innereRauhheit?: InnereRauhheit
}

export interface HumusAuflage {
	ol?: number,
	of?: number,
	oh?: number
}

export interface HumusForm {
	aufnahmeFlaeche: AltNeu<string>,
	gesamtFlaeche?: AltNeu<string>,
	morphologisch?: AltNeu<string>
}

export interface StandortFormenGruppe {
	naehrkraftStufe: string,
	stammFeuchteStufe: Pair<string>,
	substratGruppe: string,
	/**
	 * Zuletzt automatisch vorgeschlagene Substratgruppe (aus der Feinbodenform,
	 * siehe evaluators/substratgruppe.ts). Stimmt sie mit `substratGruppe`
	 * ueberein, stammt der Wert aus dem Vorschlag und darf bei einem Wechsel der
	 * Bodenform nachgezogen werden. Weicht sie ab (oder fehlt sie, z.B. bei
	 * Altdaten und Importen), gilt der Wert als von Hand gesetzt und bleibt
	 * unangetastet.
	 */
	substratGruppeVorschlag?: string,
	zustands?: AltNeu<string>,
	imissions?: AltNeu<string>
}

export interface MosaikBereich {
	nummer: string,
	name: string,
	haupttyp: string
}

export interface Pair<T> {
	first: T,
	second: T
}

export interface Baumart {
	art: string,
	alter: number,
	hoehe: Pair<number>,
	durchmesser: Pair<number>,

	Bonitaet: number,
	qualitaet: string
}

export interface MoorTyp {
	oekologisch: string,
	hydrogenetisch: string
}

export interface BodenformPair {
	trenner: '/'|'//'|'',
	hbf: string,
	fbf: string,
	/** Pro-Glied-Präfix (Aufeinanderfolge aus b, g, h, o, p, q, s, Ap, po, rb, tp, w, a, x — Bedeutungen in bohrpunkte/help/fbf-praefixe.ts). Hat nichts mit „begraben" zu tun; begraben ist ein Glied bereits dadurch, dass es als zweites Glied existiert. */
	prefix?: string
}

export interface Bodenform {
	bf: BodenformPair[],
	suffix?: string,
	/** @deprecated Globaler Präfix — vor v10. Wird durch Migration nach bf[0].prefix verschoben. */
	prefix?: string,
	/** Reliktischer Go-Horizont — wird nach der Wasserform als "(G)" angezeigt */
	reliktG?: boolean
}

// Rohdaten der Wetterdaten für spätere Neuinterpretation.
// Historisch („Datum"): Mittel bzw. häufigster Wert der Arbeitszeit 10 bis 16 Uhr
// am Aufnahmetag (seit 15.09.2026; davor Tageswerte samt Nacht). Aktuell („Jetzt"):
// der Wert zum Abrufzeitpunkt. Der Niederschlag ist immer eine Summe.
export interface WitterungRohdaten {
	temperature: number;        // Temperatur in °C
	precipitation: number;      // Niederschlag in mm (Tagessumme bzw. heute bis jetzt)
	cloudCover: number;         // Bewölkung in %
	weatherCode: number;        // WMO Wettercode
	windSpeed: number;          // Windgeschwindigkeit in km/h
	windDirection: number;      // Windrichtung in Grad
	humidity: number;           // Relative Luftfeuchtigkeit in %
	precipitationDays: number;  // Niederschlag über konfigurierte Tage in mm
	daysUsed: number;           // Anzahl Tage für Niederschlagsberechnung
	fetchedAt?: string;         // Zeitpunkt des Abrufs (ISO-String)
	isCurrentWeather?: boolean; // true = aktuelles Wetter, false = historisch
}

export interface ExtendedHeader {
	geologie: Geologie,
	morphologie: string[],

	grundgesteinSediment?: string,
	relief: Relief,

	witterung?: string,
	witterungRohdaten?: WitterungRohdaten,

	klimaStufe: AltNeu<'' | 'f' | 'lf' | 'lm' | 'm' | 'mt' | 't' | 'tt'>,

	klimabereich?: AltNeu<string>,
	wuchsgebiet?: string,
	wuchsbezirk?: string,

	humusAuflage: HumusAuflage,
	bestockungsTyp?: AltNeu<string>,

	stfg: StandortFormenGruppe
	mosaikBereich?: MosaikBereich

	standortklimatischeAngaben?: string,
	mesorelief?: string,
	lage?: string,

	hangrichtung?: 'N' | 'NNO' | 'NO' | 'ONO' | 'O' | 'OSO' | 'SO' | 'SSO' | 'S' | 'SSW' | 'SW' | 'WSW' | 'W' | 'WNW' | 'NW' | 'NNW',
	hangneigung?: number,
	meereshoehe?: number,

	vorbestandlicheGeschichte?: string,
	forstlicheBodenbearbeitung?: string,
	anthropogeneBeeinflussung?: string,
	duengemittelArt?: string,
	herbizide?: string,
	insektizide?: string,
	grundStauwasserVeraenderungen?: string,

	baumarten: Baumart[] | string,

	/**
	 * Strukturierte, SEA-konforme Bestand-Beschreibung (vom Bestand-Konverter
	 * erzeugt). Optional: wird nur gesetzt, wenn der Punkt konvertiert wurde.
	 * Der kanonische Text liegt zusätzlich in `baumarten` (String) für Export/PDF.
	 */
	bestand?: BestandBeschreibung,

	/**
	 * Freitext-Zusatz zum Bestand: hält bei Import/Altdaten den ursprünglichen
	 * Prosa-Text, bei neuen Punkten zusätzliche Infos, die nicht in die strikte
	 * SEA-Form (`baumarten`) passen.
	 */
	bestandFreitext?: string,

	/**
	 * Vom Nutzer bestätigte, bewusst von der SEA-Form abweichende Bestand-
	 * Beschreibung. Hält exakt den bestätigten Text; stimmt er mit `baumarten`
	 * überein, wertet die Validierung den (sonst gelben) Bestand als „ok".
	 * Wird beim Ändern des Bestand-Texts automatisch unwirksam (Textvergleich).
	 */
	bestandBestaetigt?: string,

	begruendungsart?: string,
	mischform?: string,
	waldfunktion?: string,

	entwaesserungsverhalten: ''|'I'|'II'|'III',
	/**
	 * Zuletzt automatisch vorgeschlagener Wert für entwaesserungsverhalten.
	 * Stimmt er mit dem Feld überein, stammt der Wert aus dem Vorschlag und darf
	 * bei Änderungen an der Substratfolge nachgezogen werden. Weicht er ab (oder
	 * fehlt er, z.B. bei Altdaten und Importen), gilt der Wert als von Hand
	 * gesetzt und bleibt unangetastet.
	 */
	entwaesserungVorschlag?: ''|'I'|'II'|'III',

	kMgCaPSerie: string,
	vWertSaugsaum?: number,
	vWertSaugsaumVermutet?: '<15' | '15-40' | '>40',

	moorTyp?: MoorTyp,

	nWsk?: number,
	nWskStufe: string,

	saugsaum?: number,
	/**
	 * Kalktiefe [cm] (Karbonattiefe). Im Kurzprotokoll von Infopunkt,
	 * Halbtiefbohrung und Grenzaufschluss ohne Protokoll (9990/9998/9999) von
	 * Hand eingegeben, siehe `istPunktartMitKalkSpiegeltiefe`. Im vollen
	 * Protokoll kommt die Karbonattiefe aus der Substratfolge (`karbonatEval`);
	 * dieses Feld fuellt dort nur der Import.
	 */
	karbonat?: number,
	/** Spiegeltiefe [cm]: Boden-Maske des vollen Protokolls und Kurzprotokoll (9990/9998/9999). */
	spiegelTiefe?: number,
	mesoklima: ''|'fr'|'tr',

	gHorizont: {
			go: string, gr: string, g: string
	},

	sHorizont: { sw: string, sd: string, s: string },

	ph?: {
		wert1?: number,
		ort1?: '' | 'Oberboden' | 'Saugsaum',
		wert2?: number,
		ort2?: '' | 'Oberboden' | 'Saugsaum',
		durchschnitt?: number
	}
}

export interface BodenVegetation {
	// bestandsBeschreibung: string,
	groesseDerAufnahmeFlaeche?: string,
	aufnahme: BodenVegetationsAufnahme
}

export interface BodenVegetationsAufnahme {
	schichtAnteile: SchichtAnteile,
	vegetationen: BodenvegetationZeile[]
}

export interface SchichtAnteile {
	b1?: number,
	b2?: number,
	str?: number,
	kr1?: number,
	kr2?: number,
	m?: number
}

export interface BodenvegetationZeile {
	/** Kennung der Zeile. Fehlt bei Zeilen von vor dem 29.08.2026, siehe {@link neueZeilenKennung}. */
	id?: string,
	schicht?: 'b1'|'b2'|'str'|'kr1'|'kr2'|'m',
	haeufigkeit: string,
	vegetation: string,
}
	
export function schichtLabel(schicht: 'b1'|'b2'|'str'|'kr1'|'kr2'|'m'): string {
	switch (schicht) {
		case 'b1': return 'Baumschicht 1';
		case 'b2': return 'Baumschicht 2';
		case 'str': return 'Strauchschicht';
		case 'kr1': return 'Krautschicht 1';
		case 'kr2': return 'Krautschicht 2';
		case 'm': return 'Moosschicht';
		default: return schicht;
	}
}

export type Punktart = string;

/**
 * Numerische ID einer Punktart. Robust gegen beide Datenformate:
 * - neues Format: reine Nummer ("6", "9991")
 * - Alt-Format:   "<nummer>_<beschreibung>" ("6_Tiefbohrung")
 * parseInt liest die fuehrenden Ziffern und stoppt am ersten Nicht-Ziffern-Zeichen.
 */
export function idOf(punktart: string) {
	return parseInt(String(punktart ?? ''), 10);
}

/**
 * Eine Zeile der Punktarten-Liste (zentraler ListStore, DB `bpweb-listen`).
 * `nummer` ist der stabile Schluessel (= `bp.type`). `svg` ist der Dateiname des
 * mitgelieferten Symbols; `svgData` (optional) ein hochgeladenes Symbol als
 * Data-URL, das den Asset-Pfad ueberschreibt. `kategorie` gruppiert die Legende.
 */
export interface PunktartRow {
	nummer: number;
	beschreibung: string;
	svg?: string;
	svgData?: string | null;
	kategorie?: string;
}

/**
 * Modul-Lookup Nummer -> Punktart-Zeile. Wird vom DataService aus dem zentralen
 * ListStore gepusht (setPunktartList in applyStoreDerived), analog
 * OptionService.setHumusFormList. So sind Beschreibung/SVG ueberall (Karte,
 * Liste, Legende) Single Source und folgen den Editor-Edits.
 */
let PUNKTART_LOOKUP = new Map<number, PunktartRow>();
/** Reverse-Lookup Beschreibung -> Nummer (fuer die Typeahead-Auswahl). */
let PUNKTART_BY_BESCHREIBUNG = new Map<string, number>();

export function setPunktartList(rows: PunktartRow[] | undefined | null): void {
	const map = new Map<number, PunktartRow>();
	const byBeschreibung = new Map<string, number>();
	for (const r of rows ?? []) {
		if (r && typeof r.nummer === 'number') {
			map.set(r.nummer, r);
			const b = (r.beschreibung ?? '').trim();
			// Erste Zeile gewinnt bei (unerwarteten) doppelten Beschreibungen.
			if (b && !byBeschreibung.has(b)) byBeschreibung.set(b, r.nummer);
		}
	}
	PUNKTART_LOOKUP = map;
	PUNKTART_BY_BESCHREIBUNG = byBeschreibung;
}

/**
 * Nummer-String (= bp.type) zu einer Beschreibung. Fuer die Typeahead-Auswahl,
 * wo der Nutzer eine Beschreibung waehlt und die Komponente daraus den type
 * (Nummer) setzt. Liefert '' wenn die Beschreibung unbekannt ist.
 */
export function punktartNummerFromBeschreibung(beschreibung: string): string {
	const n = PUNKTART_BY_BESCHREIBUNG.get((beschreibung ?? '').trim());
	return n === undefined ? '' : String(n);
}

/**
 * Anzeige-Label einer Punktart fuer Dropdowns: "Beschreibung (Nummer)" — so ist
 * der Schluessel (Nummer) sichtbar und per Tippen suchbar. Ohne Beschreibung nur
 * die Nummer.
 */
export function punktartDisplayLabel(punktart: string): string {
	const n = idOf(punktart);
	const b = punktartBeschreibung(punktart);
	if (!b) return Number.isFinite(n) && n > 0 ? String(n) : '';
	return `${b} (${n})`;
}

/**
 * Nummer-String (= bp.type) aus einem Anzeige-Label. Akzeptiert
 * "Beschreibung (6)", "Beschreibung" oder die reine Nummer "6". Liefert nur
 * gueltige (in der Liste vorhandene) Nummern, sonst ''.
 */
export function punktartNummerFromLabel(label: string): string {
	const s = String(label ?? '').trim();
	// 1) Klammer-Nummer am Ende ("… (6)")
	const m = /\((\d+)\)\s*$/.exec(s);
	if (m && PUNKTART_LOOKUP.has(Number(m[1]))) return m[1];
	// 2) reine Nummer
	if (/^\d+$/.test(s) && PUNKTART_LOOKUP.has(Number(s))) return s;
	// 3) Beschreibung (ohne evtl. Klammer-Suffix)
	return punktartNummerFromBeschreibung(s.replace(/\s*\(\d+\)\s*$/, '').trim());
}

/**
 * Validierungs-Hinweis fuer einen Punktart-Feldwert (Label "Beschreibung (Nummer)",
 * Beschreibung oder reine Nummer). Leer = ok. Loest der Wert nicht auf eine in der
 * AKTUELLEN Liste vorhandene Nummer auf, wird ein Warn-Text geliefert (bewusst
 * NICHT-blockierend) — so bleiben Alt-Punkte mit entfernter/unbekannter Punktart
 * editierbar/anpassbar, statt einen harten Fehler zu werfen. Das Pflichtfeld wird
 * separat ueber `nonEmpty` geprueft, daher ist ein leerer Wert hier ok.
 */
export function punktartFieldWarning(value: string | undefined | null): string {
	const str = String(value ?? '').trim();
	if (!str) return '';
	return punktartNummerFromLabel(str) ? '' : 'Punktart nicht in der aktuellen Liste – bitte anpassen';
}

/**
 * Bringt eine Punktarten-Liste auf die Objekt-Form `PunktartRow[]`. Wandelt
 * historische String-Eintraege ("<nummer>_<beschreibung>") in Zeilen-Objekte
 * (gleiche Ableitung wie tools/build-punktarten.mjs); bereits normalisierte
 * Objekte (inkl. svgData/kategorie) bleiben unveraendert. So funktionieren
 * Editor/Typeahead/Legende auch mit einer noch nicht migrierten DB-Liste.
 */
/** Gebuendelter Seed nach Nummer — zum Anreichern alter String-Listen (Kategorie). */
const PUNKTART_SEED_BY_NUMMER = new Map<number, PunktartRow>(
	(PunktartenSeed as unknown as PunktartRow[])
		.filter(r => r && typeof r.nummer === 'number')
		.map(r => [r.nummer, r])
);

export function normalizePunktartList(data: any[] | undefined | null): PunktartRow[] {
	if (!Array.isArray(data)) return [];
	return data.map((e: any) => {
		// Objekt-Eintraege unveraendert durchreichen (Referenz erhalten -> In-Place-
		// Edit + Save im Editor funktionieren). Bereits migrierte Listen tragen die
		// Kategorie selbst.
		if (e && typeof e === 'object') {
			if (typeof e.nummer === 'number') return e as PunktartRow;
			return { ...e, nummer: idOf(String(e.nummer ?? '')) } as PunktartRow;
		}
		// String "<nummer>_<beschreibung>" (historische DB-Liste) -> Objekt, Kategorie
		// (und Fallbacks) aus dem gebuendelten Seed nach Nummer.
		const full = String(e ?? '');
		const i = full.indexOf('_');
		const nummer = idOf(full);
		const seed = PUNKTART_SEED_BY_NUMMER.get(nummer);
		return {
			nummer,
			beschreibung: i >= 0 ? full.substring(i + 1).replace(/_/g, ' ') : (seed?.beschreibung ?? ''),
			svg: full ? `${full}.svg` : (seed?.svg ?? ''),
			kategorie: seed?.kategorie ?? '',
		};
	});
}

/** Eine Legenden-Zeile (aufgeloeste Symbol-URL + Sichtbarkeitsflag). */
export interface PunktartLegendRow {
	nummer: number;
	beschreibung: string;
	svgUrl: string;
	/** 16/70 haben bewusst kein Symbol (werden auf der Karte nicht gezeichnet). */
	invisible: boolean;
	/** Fuer symbollose Punktarten (16/70): farbiges Text-Badge statt Icon in der Legende. */
	ohneSymbolLabel?: string;
	ohneSymbolColor?: string;
}
export interface PunktartLegendGroup {
	kategorie: string;
	rows: PunktartLegendRow[];
}

/**
 * Punktarten gruppiert nach `kategorie` fuer die Symbol-Legende. Die Gruppen-
 * reihenfolge folgt dem ersten Auftreten der Kategorie (nach Nummer sortiert),
 * was die bisherige Sektionsfolge der Legende reproduziert. Single Source: die
 * Liste folgt Editor-Edits + hochgeladenen SVGs.
 */
export function punktartLegendGroups(): PunktartLegendGroup[] {
	const groups: PunktartLegendGroup[] = [];
	const byKat = new Map<string, PunktartLegendGroup>();
	const rows = [...PUNKTART_LOOKUP.values()].sort((a, b) => a.nummer - b.nummer);
	for (const r of rows) {
		const kat = (r.kategorie ?? '').trim() || 'Weitere';
		let g = byKat.get(kat);
		if (!g) { g = { kategorie: kat, rows: [] }; byKat.set(kat, g); groups.push(g); }
		g.rows.push({
			nummer: r.nummer,
			beschreibung: r.beschreibung ?? '',
			svgUrl: punktartSvgUrl(String(r.nummer)),
			invisible: istPunktartOhneSymbol(String(r.nummer)),
			ohneSymbolLabel: punktartOhneSymbolLabel(String(r.nummer)),
			ohneSymbolColor: punktartOhneSymbolColor(String(r.nummer)),
		});
	}
	return groups;
}

/**
 * Legenden-Zeilen NUR fuer die uebergebenen Punktart-Typen (Strings, beide
 * Formate), eindeutig nach Nummer und aufsteigend sortiert. Fuer die Druck-
 * Layout-Legende, die nur die im Karten-Ausschnitt vorkommenden Punktarten zeigt.
 */
export function punktartLegendRowsForTypes(types: (string | undefined | null)[]): PunktartLegendRow[] {
	const nums = [...new Set(types.map((t) => idOf(t as string)).filter((n) => n > 0))].sort((a, b) => a - b);
	return nums.map((n) => ({
		nummer: n,
		beschreibung: punktartBeschreibung(String(n)),
		svgUrl: punktartSvgUrl(String(n)),
		invisible: istPunktartOhneSymbol(String(n)),
		ohneSymbolLabel: punktartOhneSymbolLabel(String(n)),
		ohneSymbolColor: punktartOhneSymbolColor(String(n)),
	}));
}

// Selbst-Initialisierung mit den gebuendelten Defaults, damit das Lookup auch vor
// der Store-Hydrierung (DataService.applyStoreDerived) gefuellt ist. Der DB-Stand
// ueberschreibt diese Defaults zur Laufzeit via setPunktartList.
setPunktartList(PunktartenSeed as unknown as PunktartRow[]);

export function punktartRow(punktart: string): PunktartRow | undefined {
	return PUNKTART_LOOKUP.get(idOf(punktart));
}

/**
 * Beschreibung einer Punktart aus dem Lookup. Fallback (Lookup leer / unbekannte
 * Nummer / Alt-Daten): der Teil hinter dem ersten '_' des Voll-Strings, '_' als
 * Leerzeichen. Reine Nummern ohne Lookup liefern ''.
 */
export function punktartBeschreibung(punktart: string): string {
	const row = punktartRow(punktart);
	if (row) return row.beschreibung ?? '';
	const s = String(punktart ?? '');
	const i = s.indexOf('_');
	return i >= 0 ? s.substring(i + 1).replace(/_/g, ' ') : '';
}

/**
 * Aufgeloeste Symbol-URL einer Punktart: hochgeladenes `svgData` hat Vorrang,
 * sonst der Asset-Pfad aus der `svg`-Spalte. Fallback fuer Alt-Daten ohne
 * Lookup: der Voll-String als Dateiname (bisherige Konvention).
 */
export function punktartSvgUrl(punktart: string): string {
	const row = punktartRow(punktart);
	if (row?.svgData) return row.svgData;
	if (row?.svg) return `assets/img/points/${row.svg}`;
	// Kein Listen-Eintrag: Alt-String ("<nr>_<name>") zeigt per Konvention weiter
	// auf das gleichnamige Legacy-Asset (Abwaertskompatibilitaet). Eine reine
	// Nummer ohne Zeile (entfernte/unbekannte Punktart eines Alt-Punkts) hat KEIN
	// passendes Asset -> Platzhalter, damit die Karte kein 404/leeres Symbol zeigt.
	const s = String(punktart ?? '');
	if (s.includes('_')) return `assets/img/points/${s}.svg`;
	return 'assets/img/points/placeholder.svg';
}

// Mapping von SYMBOL-ID zu Punktart-String (für Import aus GPKG/GeoJSON)
const SYMBOL_TO_PUNKTART: Record<number, string> = {
	// Aufschlussarten (Symbol 1–7)
	1: '1_Weisergrube_ohne_Bodenanalyse',
	2: '2_Weisergrube_mit_Bodenanalyse',
	3: '3_Bodengrube_ohne_Bodenanalyse',
	4: '4_Bodengrube_mit_Bodenanalyse',
	5: '5_Humusprobe',
	6: '6_Tiefbohrung',
	7: '7_Moorpunkt',
	70: '9991_Halbtiefbohrung_mit_Protokoll',

	// Grundwasser (Symbol 8–11)
	8: '8_GW_hoeher_1',
	9: '9_GW_hoeher_2',
	10: '10_GW_tiefer_1',
	11: '11_GW_tiefer_2',

	// Staunässe (Symbol 12–13, 112–113)
	12: '12_SW_stärker',
	13: '13_SW_geringer',
	112: '112_SW_stärker_2',
	113: '113_SW_geringer_2',

	// Sonstige Standortmerkmale (Symbol 14–25)
	14: '14_kleinfl_Entwässerung',
	15: '15_Kleingewaesser',
	25: '25_kleinfl_quellig',

	// Nährkraft (Symbol 17–24)
	17: '17_Nährkraft_höher_1',
	18: '18_Nährkraft_höher_2',
	19: '19_Nährkraft_geringer_1',
	20: '20_Nährkraft_geringer_2',
	21: '21_kleinfl_karbonatisch',
	23: '23_kleinfl_organisch',
	24: '24_kleinfl_mineralisch',

	// Humusformen — Mull (Symbol 27–30)
	27: '27_fMu',
	28: '28_iMu',
	29: '29_mMu',
	30: '30_tMu',

	// Humusformen — mullartiger Moder (Symbol 31–35)
	31: '31_nMM',
	32: '32_fMM',
	33: '33_iMM',
	34: '34_mMM',
	35: '35_tMM',

	// Humusformen — Moder (Symbol 36–40)
	36: '36_nMo',
	37: '37_fMo',
	38: '38_iMo',
	39: '39_mMo',
	40: '40_tMo',

	// Humusformen — rohhumusartiger Moder (Symbol 41–45)
	41: '41_nRM',
	42: '42_fRM',
	43: '43_iRM',
	44: '44_mRM',
	45: '45_tRM',

	// Humusformen — Rohhumus (Symbol 46–50)
	46: '46_nRo',
	47: '47_fRo',
	48: '48_iRo',
	49: '49_mRo',
	50: '50_tRo',

	// Humusformen — Magerrohhumus (Symbol 51–53)
	51: '51_iMgRo',
	52: '52_mMgRo',
	53: '53_tMgRo',

	// Humusformen — Hungerrohhumus (Symbol 54–56)
	54: '54_iHuRo',
	55: '55_mHuRo',
	56: '56_tHuRo',

	// Rohboden (Symbol 57)
	57: '57_Rohboden',

	// Humusformen — Hagermoder (Symbol 58–60)
	58: '58_iHMo',
	59: '59_mHMo',
	60: '60_tHMo',

	// Humusformen — rohhumusartiger Hagermoder (Symbol 61–63)
	61: '61_iHRM',
	62: '62_mHRM',
	63: '63_tHRM',

	// Humusformen — Hagerrohhumus (Symbol 64–66)
	64: '64_iHRo',
	65: '65_mHRo',
	66: '66_tHRo',

	// Abweichung Standortszustand (Symbol 67–68)
	67: '67_pos_Abweichung',
	68: '68_neg_Abweichung',

	// Mullartiger Hagermoder (Symbol 69)
	69: '69_mMuHMo',

	// Verhagerungsgefährdung (Symbol 83–85)
	83: '83_stark_verhagerungsgefährdet',
	84: '84_mäßig_verhagerungsgefährdet',
	85: '85_schwach_verhagerungsgefährdet',

	// Kombinierte Merkmale — GW + Nährkraft (Symbol 90–104)
	90: '90_GWundNährkraft_höher_1',
	91: '91_GW_höher_1_Nährkraft_höher_2',
	93: '93_GW_tiefer_1_Nährkraft_höher_1',
	94: '94_GW_tiefer_2_Nährkraft_tiefer_1',
	95: '95_GW_tiefer_1_Nährkraft_tiefer_1',
	96: '96_GW_tiefer_1_Nährkraft_tiefer_2',
	97: '97_GW_höher_1_Nährkraft_tiefer_1',
	98: '98_GW_höher_2_Nährkraft_tiefer_1',
	99: '99_GW_tiefer_2_Nährkraft_höher_1',
	100: '100_GW_höher_2_Nährkraft_tiefer_2',
	101: '101_GW_tiefer_2_Nährkraft_tiefer_2',
	102: '102_GW_höher_2_Nährkraft_höher_1',
	103: '103_GW_höher_2_Nährkraft_höher_2',
	104: '104_GW_höher_1_Nährkraft_tiefer_2',

	// Substratfeuchte (Symbol 120–125)
	120: '120_Substratfeuchte_1_feuchter',
	121: '121_Substratfeuchte_2_feuchter',
	122: '122_Substratfeuchte_3_feuchter',
	123: '123_Substratfeuchte_1_trockener',
	124: '124_Substratfeuchte_2_trockener',
	125: '125_Substratfeuchte_3_trockener',

	// Kombinierte Merkmale — Nährkraft + Staunässe (Symbol 130–149)
	130: '130_Nährkraft_höher_1_SW_stärker_1',
	131: '131_Nährkraft_höher_1_SW_geringer_1',
	132: '132_Nährkraft_höher_1_SW_stärker_2',
	133: '133_Nährkraft_höher_1_SW_geringer_2',
	134: '134_Nährkraft_höher_2_SW_stärker_1',
	135: '135_Nährkraft_höher_2_SW_geringer_1',
	136: '136_Nährkraft_höher_2_SW_stärker_2',
	137: '137_Nährkraft_höher_2_SW_geringer_2',
	138: '138_Nährkraft_geringer_1_SW_stärker_1',
	139: '139_Nährkraft_geringer_1_SW_geringer_1',
	140: '140_Nährkraft_geringer_1_SW_stärker_2',
	141: '141_Nährkraft_geringer_1_SW_geringer_2',
	142: '142_Nährkraft_geringer_2_SW_stärker_1',
	143: '143_Nährkraft_geringer_2_SW_geringer_1',
	144: '144_Nährkraft_geringer_2_SW_stärker_2',
	145: '145_Nährkraft_geringer_2_SW_geringer_2',
	146: '146_karbonatisch_SW_stärker_1',
	147: '147_karbonatisch_SW_geringer_1',
	148: '148_karbonatisch_SW_stärker_2',
	149: '149_karbonatisch_SW_geringer_2',

	// Sondertypen (app-spezifisch)
	150: '150_Sonderstandort',
	9990: '9990_Infopunkt',
	9991: '9991_Halbtiefbohrung_mit_Protokoll',
	9996: '9990_Infopunkt',
	9998: '9998_Halbtiefbohrung',
	9999: '9999_Grenzaufschluss'
};

export function punktartFromSymbol(symbol: number): Punktart | undefined {
	return SYMBOL_TO_PUNKTART[symbol];
}

export function istPunktartMitProtokoll(punktart: Punktart): boolean {
	return idOf(punktart) === 6 || idOf(punktart) === 7 || idOf(punktart) === 9991 || (idOf(punktart) >=1 && idOf(punktart) <= 4);
}

export function istPunktartMitNummer(punktart: Punktart): boolean {
	return new Set([1, 2, 3, 4, 5, 6, 7, 9990, 9991]).has(idOf(punktart));
}

// Symbole, deren Pfeilrichtung frei wählbar ist und in der Karte gedreht wird
// (Verhagerungsgefährdung 83–85). Die Pfeile sind im SVG nach Osten gezeichnet,
// d.h. rotation 0 = Pfeile zeigen nach Osten.
// Bewusst NICHT enthalten: GW-/Staunässe-Pfeile (8–13, 112/113 …), bei denen die
// Pfeilrichtung die fachliche Aussage kodiert und nicht gedreht werden darf.
const DREHBARE_SYMBOLE = new Set([83, 84, 85]);

export function istPunktartDrehbar(punktart: Punktart): boolean {
	return DREHBARE_SYMBOLE.has(idOf(punktart));
}

/**
 * Punktarten, die KEIN Symbol bekommen und nur ueber ihr Label dargestellt
 * werden: 16 (Einsprengung andere Bodenform) und 70 (kleinfl. abweichende
 * Humusform). Single Source fuer Karte (Lib `isInvisibleType`), Legenden
 * (`PunktartLegendRow.invisible`) und Punkt-Liste (Text-Badge `[16]`/`[70]`
 * statt Icon).
 */
export function istPunktartOhneSymbol(punktart: Punktart): boolean {
	const id = idOf(punktart);
	return id === 16 || id === 70;
}

/**
 * Kurz-Kuerzel fuer das Listen-Badge einer symbollosen Punktart (statt Icon):
 * 16 -> "FBF" (Einsprengung andere Bodenform), 70 -> "HF" (abweichende
 * Humusform) — analog den Karten-Seitenlabels. '' fuer Punktarten mit Symbol.
 */
export function punktartOhneSymbolLabel(punktart: Punktart): string {
	const id = idOf(punktart);
	if (id === 16) return 'FBF';
	if (id === 70) return 'HF';
	return '';
}

/**
 * Farbe des Legenden-Badges einer symbollosen Punktart — spiegelt die
 * Karten-Seitenlabel-Farben (16 = Bodenform-Kontext rot, 70 = Humusform-
 * Kontext gruen). '' fuer Punktarten mit Symbol.
 */
export function punktartOhneSymbolColor(punktart: Punktart): string {
	const id = idOf(punktart);
	if (id === 16) return '#cc0000';
	if (id === 70) return '#228B22';
	return '';
}

export function bohrpunktNummerLabel(punktart: Punktart): string {
	if (idOf(punktart) === 9990) return 'Bemerkungen';
	return 'Nummer';
}

export function punktLabel(bp: AufnahmePunktDocument, useType = false): string {
	const typeDisplayString = (type: string) => `${punktartBeschreibung(type)} (${idOf(type)})`;
	if (istPunktartMitNummer(bp.type)) {
		if (!bp.header.nummer) {
			return useType ? typeDisplayString(bp.type) : '';
		}
		else
			return String(bp.header.nummer);
	}
	else if (useType)
		return typeDisplayString(bp.type);
	return '';
}

export function istPunktartMitKurzprotokoll(punktart: Punktart): boolean {
	// Alle Punktarten ohne volles Protokoll bekommen ein Kurzprotokoll
	return !istPunktartMitProtokoll(punktart);
}

/**
 * Punktarten, deren Kurzprotokoll Kalktiefe und Spiegeltiefe fuehrt (Nutzer-Auftrag
 * 24.09.2026): Infopunkt (9990), Halbtiefbohrung ohne Protokoll (9998) und
 * Grenzaufschluss/Bodengrube ohne Protokoll (9999). Die uebrigen Kurzprotokoll-
 * Punktarten (Standortmerkmale) bekommen die Felder nicht. Gespeichert wird in den
 * Feldern des vollen Protokolls: `extendedHeader.karbonat` (Kalktiefe) und
 * `extendedHeader.spiegelTiefe` - kein zweites Feld fuer dieselbe Sache. Der
 * GIS-Export traegt beide Spalten fuer ALLE Punktarten (`gis-export-tiefen.ts`).
 */
export function istPunktartMitKalkSpiegeltiefe(punktart: Punktart): boolean {
	return new Set([9990, 9998, 9999]).has(idOf(punktart));
}

export const isValidNumber = (n: number) => !isNaN(n) && isFinite(n);
export const isValidLatLng = (l: LatLng) => isValidNumber(l.lat) && isValidNumber(l.lng);
export const isValidBounds = (b: LatLngBounds) => isValidLatLng(b.getNorthEast()) && isValidLatLng(b.getSouthWest());

export interface LastModified {
	time: number,
	user: string
}

export interface ImportInfo {
	time: number,              // Timestamp des Imports
	user?: string,             // Nutzer, der den Import gestartet hat
	source?: string,           // Quelldatei (z.B. "LH001_LH030.xlsx / BPF (lh001)")
	coordTransform?: string,   // Koordinaten-Transformation (z.B. "GK Zone 4 → EPSG:5650")
}

export interface AufnahmePunkt {
	version?: number,
	type: Punktart,
	/**
	 * Drehung richtungsabhängiger Symbole (z.B. Verhagerungspfeile) in Grad
	 * im Uhrzeigersinn, bezogen auf die im SVG gezeichnete Ausrichtung
	 * (0/undefined = wie gezeichnet). Nur für drehbare Punktarten gesetzt,
	 * siehe istPunktartDrehbar().
	 */
	rotation?: number,
	quality: number,
	header: Header,
	extendedHeader: ExtendedHeader,
	Substratfolge: SubstratZeile[];
	Horizontfolge: HorizontZeile[];
	bodenvegetation: BodenVegetation;
	lastModified?: LastModified;
	importInfo?: ImportInfo;
	/** Eingebettete Historie des Batch-Edit-Assistenten (max. 20 Eintraege, FIFO). */
	batchEditHistory?: BatchEditHistoryEntry[];
	/**
	 * Kartierverfahren des Punktes. **Fehlt der Vermerk, ist es SEA 95**: so sehen
	 * alle Punkte von vor der Einführung aus, und neue SEA-Punkte bekommen auch
	 * keinen. Gelesen wird immer über `verfahrenVon()` (bohrpunkte/verfahren/verfahren.ts),
	 * nie direkt. Gesetzt wird der Vermerk beim Anlegen nach dem Projekt
	 * (`richtePunktNachProjektAus`).
	 *
	 * Neues Feld auf dieser Ebene: `createNew()` im Dokument-Handler muss es
	 * ausdrücklich mitnehmen, sonst geht es beim Anlegen und Kopieren still verloren.
	 * In der Index-Sicht (Karte, Listen) steht es NICHT; dort wird es bisher nicht
	 * gebraucht.
	 */
	verfahren?: Kartierverfahren;
	/** Auflage der Bodenkundlichen Kartieranleitung. Nur bei `verfahren: 'ka'`. */
	kaAuflage?: KaAuflage;
	/** Die Aufnahme nach der Bodenkundlichen Kartieranleitung. Nur bei `verfahren: 'ka'`. */
	ka?: KaAufnahme;
}

export interface Gefuege {
	form: string,
	groesse: string,
	auspraegung: string
}

export interface Feindurchwurzelung {
	dichte: string,
	haeufung: number,
}

/**
 * Kennung für eine **neue** Zeile einer Zeilenliste (Substratfolge,
 * Horizontfolge, Bodenvegetation).
 *
 * Wozu: Ohne Kennung lässt sich dieselbe Zeile in zwei Fassungen desselben
 * Punktes nicht wiederfinden. Der Platz in der Liste taugt dafür nicht, zwei
 * getrennte Aufnahmen stehen beide auf Platz 1. Deshalb konnte man bei zwei
 * Fassungen bisher nur die ganze Liste übernehmen. Mit Kennung ordnet der
 * Vergleich die Zeilen einander zu und man entscheidet zeilenweise.
 *
 * **Nur für neue Zeilen.** Alte Zeilen nachträglich zu kennzeichnen wäre
 * schädlich: Zwei Geräte vergäben unabhängig voneinander zwei verschiedene
 * Kennungen für dieselbe Zeile, und danach sähe es aus wie zwei verschiedene
 * Zeilen. Alte Zeilen laufen weiter über die Ganz-oder-gar-nicht-Regel.
 *
 * `randomUUID` gibt es nur im sicheren Kontext; ältere WebViews bekommen einen
 * Rückfall aus Zeit und Zufall. Beides ist für diesen Zweck eindeutig genug.
 */
export function neueZeilenKennung(): string {
	const c: any = (globalThis as any).crypto;
	if (typeof c?.randomUUID === 'function') return c.randomUUID();
	return `z-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Kopien der Zeilen mit **frischen** Kennungen.
 *
 * Für das Kopieren eines Punktes (neuer Punkt „mit Horizonten"): Die Zeilen
 * gehören dann zu einem eigenen Datensatz und dürfen nicht die Kennungen des
 * Vorbilds tragen. Die Vorlage bleibt unangetastet.
 */
export function frischeZeilenKennungen<T extends { id?: string }>(
	zeilen: T[] | null | undefined,
): T[] {
	if (!Array.isArray(zeilen)) return [];
	return zeilen.map(z => ({ ...z, id: neueZeilenKennung() }));
}

export interface SubstratZeile {
	/** Kennung der Zeile. Fehlt bei Zeilen von vor dem 29.08.2026, siehe {@link neueZeilenKennung}. */
	id?: string,
	tiefe: number,
	maechtigkeit: number,
	substrat: string,
	wechselschichtung: boolean,
	humusgehalt?: number,
	lagerungsdichte?: number,
	festigkeit?: number,
	skelettgehalt: number,
	kiesgehalt?: number,
	steingehalt?: number,
	karbonatgehalt: number,
	nWsk?: number,
	gefuege?: Gefuege,
	feindurchwurzelung?: Feindurchwurzelung,
	torfarten: string[],
	torfZersetzungsgrad?: number,
	muddeKonsistenz?: number,
	substanzvolumen?: number,
	farbe?: string,
	/** Lage der Schicht (Grube-Profil); Name vermeidet Kollision mit ExtendedHeader.lage = "Lage der Grube". */
	schichtLage?: string,
	perstruktionszone?: string,
	/** Substratherkunft, z. B. "glz" (glazial), "fl" (fluviatil), "äol" — SEA Spalte links der Perstruktionszone. */
	substratherkunft?: string,
	/** Probeentnahme im Profil — Spanne in cm, Flaeche cm^2, Volumen cm^3. */
	probeentnahme?: {
		spanne?: string,
		flaeche?: number,
		volumen?: number
	},
	besonderheiten: string
}

export interface HorizontZeile {
    /** Kennung der Zeile. Fehlt bei Zeilen von vor dem 29.08.2026, siehe {@link neueZeilenKennung}. */
    id?: string,
    /**
     * Tiefe und Maechtigkeit duerfen fehlen (Nutzer-Entscheidung 19.09.2026):
     * Eine frisch angelegte Zeile entsteht leer und wird erst ausgefuellt. Bis
     * dahin behauptete der Typ das Gegenteil, und die Stellen, die eine Zeile
     * anlegen, kamen nur mit `any` oder `as HorizontZeile` daran vorbei.
     */
    tiefe?: number,
    maechtigkeit?: number,
    horizonte: string[]
}

export enum ValidationLevel {
	ok = 0,
	warning = 1,
	danger = 2
};

export interface CheckerComment {
	name: string,
	time: number,
	msg: string
}

export interface CheckerComments {
	resolved: boolean,
	type: ValidationLevel,
	comments: CheckerComment[];
}

export interface ValidationState {
	resolved?: boolean;
	dataSeverity: ValidationLevel;
	commentsSeverity: ValidationLevel;
	hasDiskussions: boolean;
}

export interface AufnahmePunktDocument extends AufnahmePunkt {
	_id: string,
	state: ValidationState;
	checkerComments : Map<string,CheckerComments>;
	/** Punkt-Workflow (Status, History, Erzeuger). Optional: Bestandsdaten haben das Feld nicht. */
	workflow?: WorkflowState;
}

export function roundToString(v?: number, digits = 2): string | undefined {
	if (istLeer(v)) return undefined;
	const pow = Math.pow(10, digits);
	const n = Math.round((v! + Number.EPSILON) * pow) / pow;
	return n.toFixed(digits).replace('.', ',');
}

export function roundToNumber(v?: number, digits = 2): number | undefined {
	if (!v && v !== 0) return;
	const pow = Math.pow(10, digits);
	return Math.round((v! + Number.EPSILON) * pow) / pow;
}

export const punktTemplate = (typ = '6_Tiefbohrung') => {
	const header: Header = {
		nummer: '',
		forstamt: '',
		forstbetrieb: '',
		revier: '',
		adresse: {
			gemarkung: '',
			abteilung: '',
			unterabteilung: '',
			teilflaeche: ''
		},
		koordinaten: {
			system: LocationSystem.EPSG5650,
			x: 0,
			y: 0
		},
		aufnahme: {
			datum: (new Date(Date.now())).toISOString(),
			kartierer: ''
		},
		bodenform: { bf: [{
			trenner: '',
			hbf: '',
			fbf: '',
		}], prefix: '', suffix: ''},
		humusform: {
			aufnahmeFlaeche: { neu: '' }
		},
		vegetationsformengruppe: '',

		substratFeuchte: '',
		bemerkungen: '',
		projects: [],
	};
	const extendedHeader: ExtendedHeader = {
		geologie: {
			oben: '',
			trenner: undefined,
			unten: undefined,
		},
		morphologie: [ '', '' ],
		relief: {
			relief: ''
		},
		humusAuflage: {
		},
		klimaStufe: { neu: '' },
		stfg: {
			stammFeuchteStufe: {
				first: '',
				second: ''
			},
			naehrkraftStufe: '',
			substratGruppe: ''
		},
		baumarten: '',
		entwaesserungsverhalten: '',
		entwaesserungVorschlag: '',
		kMgCaPSerie: '', // vielleicht ableitbar aus Bodenform

		nWsk: 0,
		nWskStufe: '',

		mesoklima: '',

		gHorizont: {
			go: '',
			gr: '',
			g: '',
		},
		sHorizont: {
			sw: '',
			sd: '',
			s: '',
		},

		ph: {
			wert1: undefined,
			ort1: '',
			wert2: undefined,
			ort2: '',
			durchschnitt: undefined
		},
	};
	const Substratfolge = new Array<SubstratZeile>();
	const Horizontfolge = new Array<HorizontZeile>();
	const bodenvegetation: BodenVegetation = {/*bestandsBeschreibung: '', */aufnahme: { schichtAnteile: {}, vegetationen: new Array<BodenvegetationZeile>() }};
	return {
		version: CURRENT_VERSION,
		// type ist seit v16 die reine Nummer; akzeptiert wird hier auch ein
		// Alt-/Voll-String (z.B. aus punktartFromSymbol) und wird normalisiert.
		type: String(idOf(typ)),
		quality: 1,
		header: header,
		extendedHeader: extendedHeader,
		Substratfolge: Substratfolge,
		Horizontfolge: Horizontfolge,
		bodenvegetation: bodenvegetation,
	} as AufnahmePunktDocument;
}

/**
 * Klimastufe zur ANZEIGE: Unter „Naturraum" steht links die Klimastufe von der
 * Standortkarte (WMS, `neu`) und rechts daneben die von Hand eingetragene (`alt`).
 * Beide bleiben gespeichert; angezeigt wird der Handeintrag, sonst der automatisch
 * geholte Wert. Der Handeintrag ist die Angabe des Aufnehmenden vor Ort.
 */
export function klimastufeAnzeige(klimaStufe?: AltNeu<string>): string {
	return klimaStufe?.alt?.trim() || klimaStufe?.neu?.trim() || '';
}

export function stfgString(bp: AufnahmePunktDocument) {
    const stfg = bp.extendedHeader.stfg;
    if (!stfg) return '';

    const f1 = stfg.stammFeuchteStufe?.first;
    const nk = stfg.naehrkraftStufe;
    const f2 = stfg.stammFeuchteStufe?.second;

    // Wenn keiner der Hauptwerte vorhanden ist, leere Zelle
    if (!f1 && !nk && !f2 && !stfg.substratGruppe) return '';

		const suffix = bp.header.bodenform?.suffix || '';
		const klimastufe = klimastufeAnzeige(bp.extendedHeader.klimaStufe);
		const kst = klimastufe ? `_${klimastufe}` : '';
		const sf = bp.header.substratFeuchte ? `_${bp.header.substratFeuchte}` : '';

		// Stammfeuchtestufe nur anzeigen, wenn mindestens ein Teil vorhanden ist
		const stammFeuchte = (f1 || nk || f2) ? `${f1 ?? ''}${nk ?? ''}${f2 ?? ''}` : '';
    return stammFeuchte + (stfg.substratGruppe ? `(${stfg.substratGruppe}${suffix})${sf}${kst}` : '');
  }
